
import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  useDisclosure,
  useBreakpointValue,
  Image,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import CocacolaLogo from "./OrgIndiaLogo";
import "../../styles/layout/SidebarStyles.css";
import { OrgIndiaLogo } from "./OrgIndiaLogo";
import OrgLogo from "../../assets/img/ORG.png";
import FactsLogo from "../../assets/img/Facts.jpg";

// ─── Icon registry ────────────────────────────────────────────────────────────
import {
  HomeIcon,
  StatsIcon,
  DocumentIcon,
  RocketIcon,
  InventoryIcon,
  AbstractReportIcon,
  DetailReportIcon,
  AccountIcon,
  ModelTestIcon,
  ReportIcon,
  DetailViewIcon,
  CountIcon,
  SourceUpdateIcon,
  ComparisonIcon,
  PersonIcon,
  ClockIcon,
  DownloadIcon,
} from "components/Icons/Icons";

const ICON_MAP = {
  HomeIcon: <HomeIcon color="inherit" />,
  StatsIcon: <StatsIcon color="inherit" />,
  DocumentIcon: <DocumentIcon color="inherit" />,
  RocketIcon: <RocketIcon color="inherit" />,
  InventoryIcon: <InventoryIcon color="inherit" />,
  AbstractReportIcon: <AbstractReportIcon color="inherit" />,
  DetailReportIcon: <DetailReportIcon color="inherit" />,
  AccountIcon: <AccountIcon color="inherit" />,
  ModelTestIcon: <ModelTestIcon color="inherit" />,
  ReportIcon: <ReportIcon color="inherit" />,
  DetailViewIcon: <DetailViewIcon color="inherit" />,
  CountIcon: <CountIcon color="inherit" />,
  SourceUpdateIcon: <SourceUpdateIcon color="inherit" />,
  ComparisonIcon: <ComparisonIcon color="inherit" />,
  PersonIcon: <PersonIcon color="inherit" />,
  ClockIcon: <ClockIcon color="inherit" />,
  DownloadIcon: <DownloadIcon color="inherit" />,
};

// ─── Transform API menu → sidebar items ───────────────────────────────────────
function buildMenuTree(apiMenu = []) {
  const grouped = {};
  const order = [];

  apiMenu.forEach((item) => {
    const key = item.mainMenuId;
    if (!grouped[key]) {
      grouped[key] = {
        mainMenuId: key,
        name: item.mainMenu,
        icon: ICON_MAP[item.icon] || <HomeIcon color="inherit" />,
        children: [],
      };
      order.push(key);
    }
    grouped[key].children.push(item);
  });

  return order.map((key) => {
    const group = grouped[key];
    const children = group.children;

    // Single child whose subMenuName matches the mainMenu → flat nav item
    if (
      children.length === 1 &&
      children[0].subMenuName === group.name
    ) {
      const child = children[0];

      // ── External link: type === "external" ──────────────────────────
      if (child.type === "external") {
        return {
          name: child.subMenuName,
          icon: ICON_MAP[child.icon] || <HomeIcon color="inherit" />,
          isExternal: true,
          externalUrl: child.routingPath, // routingPath holds the full URL
          menuKey: child.menuKey,
        };
      }

      // ── Normal flat item ─────────────────────────────────────────────
      return {
        path: child.routingPath,
        name: child.subMenuName,
        icon: ICON_MAP[child.icon] || <HomeIcon color="inherit" />,
        layout: "/admin",
        menuKey: child.menuKey,
        component: child.component,
      };
    }

    // Multiple children → collapsible group
    return {
      name: group.name,
      icon: group.icon,
      state: `${group.mainMenuId}_collapse`,
      views: children.map((child) => ({
        path: child.routingPath,
        name: child.subMenuName,
        icon: ICON_MAP[child.icon] || <HomeIcon color="inherit" />,
        layout: "/admin",
        menuKey: child.menuKey,
        component: child.component,
      })),
    };
  });
}

// ─── Read menu from storage ────────────────────────────────────────────────────
export function getMenuFromStorage() {
  try {
    const raw =
      localStorage.getItem("userMenu") || sessionStorage.getItem("userMenu");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export default function Sidebar({ routes, onMinimizeChange }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [openItems, setOpenItems] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // Dynamic menu built from API response stored at login
  const [menuItems, setMenuItems] = useState([]);

  useEffect(() => {
    const apiMenu = getMenuFromStorage();
    setMenuItems(buildMenuTree(apiMenu));
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("sidebarExpanded");
    if (saved !== null) setIsExpanded(JSON.parse(saved));
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarExpanded", JSON.stringify(isExpanded));
    if (onMinimizeChange) onMinimizeChange(!isExpanded);
  }, [isExpanded, onMinimizeChange]);

  const toggleItem = (stateKey) => {
    if (!stateKey) return;
    setOpenItems((prev) => ({ ...prev, [stateKey]: !prev[stateKey] }));
  };

  const isRouteActive = (item) => {
    if (!item.path) return false;
    const fullPath = (item.layout || "/admin") + item.path;
    if (location.pathname === fullPath) return true;
    if (item.views) {
      return item.views.some(
        (child) => location.pathname === (child.layout || "/admin") + child.path
      );
    }
    return location.pathname.startsWith(fullPath);
  };

  const renderNavItem = (item, index) => {
    const hasChildren = item.views && item.views.length > 0;
    const isItemOpen = openItems[item.state || ""];
    const active = isRouteActive(item);

    const iconBox = (isActive) => (
      <Box
        p={2}
        bg={isActive ? "white" : "whiteAlpha.200"}
        color={isActive ? "#C41E3A" : "white"}
        borderRadius="10px"
        transition="all 0.2s"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minW="40px"
        minH="40px"
      >
        {item.icon}
      </Box>
    );

    // ── External link (type === "external" from API) ───────────────────────
    if (item.isExternal && item.externalUrl) {
      return (
        <Box key={index} mb={1}>
          <a href={item.externalUrl} target="_blank" rel="noopener noreferrer">
            <MotionBox
              layout
              whileHover={{ x: isExpanded ? 6 : 0 }}
              whileTap={{ scale: 0.98 }}
              cursor="pointer"
              borderRadius="14px"
              overflow="hidden"
            >
              <Flex
                align="center"
                justify={isExpanded ? "space-between" : "center"}
                px={isExpanded ? 4 : 2}
                py={3}
                bg="transparent"
                color="gray.300"
                _hover={{ bg: "whiteAlpha.100" }}
                transition="all 0.22s"
              >
                <HStack spacing={isExpanded ? 3 : 0}>
                  {iconBox(false)}
                  {isExpanded && (
                    <Text fontSize="sm" fontWeight="medium">
                      {item.name}
                    </Text>
                  )}
                </HStack>
              </Flex>
            </MotionBox>
          </a>
        </Box>
      );
    }

    // ── Collapsible group ──────────────────────────────────────────────────
    if (hasChildren) {
      return (
        <Box key={index} mb={1}>
          <MotionBox
            layout
            whileHover={{ x: isExpanded ? 6 : 0 }}
            whileTap={{ scale: 0.98 }}
            cursor="pointer"
            borderRadius="14px"
            overflow="hidden"
            onClick={() => toggleItem(item.state)}
          >
            <Flex
              align="center"
              justify={isExpanded ? "space-between" : "center"}
              px={isExpanded ? 4 : 2}
              py={3}
              bg={active ? "#C41E3A" : "transparent"}
              color={active ? "white" : "gray.300"}
              _hover={{ bg: active ? "#8B0000" : "whiteAlpha.100" }}
              transition="all 0.22s"
            >
              <HStack spacing={isExpanded ? 3 : 0}>
                {iconBox(active)}
                {isExpanded && (
                  <Text fontSize="sm" fontWeight="medium">
                    {item.name}
                  </Text>
                )}
              </HStack>
              {isExpanded && (
                <MotionBox
                  animate={{ rotate: isItemOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={16} />
                </MotionBox>
              )}
            </Flex>
          </MotionBox>

          <AnimatePresence initial={false}>
            {isItemOpen && isExpanded && (
              <MotionBox
                key="submenu"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                overflow="hidden"
              >
                <VStack align="start" spacing={1} pl={10} py={2}>
                  {item.views.map((child, i) => {
                    const childActive =
                      location.pathname ===
                      (child.layout || "/admin") + child.path;
                    return (
                      <NavLink
                        key={i}
                        to={(child.layout || "/admin") + child.path}
                        onClick={(e) => {
                          const token =
                            localStorage.getItem("authToken") ||
                            sessionStorage.getItem("authToken");
                          if (!token) {
                            e.preventDefault();
                            window.location.href = "/auth/signin";
                          }
                        }}
                      >
                        <MotionBox whileHover={{ x: 8 }} whileTap={{ scale: 0.98 }} w="full">
                          <Flex
                            align="center"
                            px={4}
                            py={2.5}
                            borderRadius="12px"
                            bg={childActive ? "#C41E3A" : "transparent"}
                            color={childActive ? "white" : "gray.400"}
                            _hover={{ bg: childActive ? "#8B0000" : "whiteAlpha.100" }}
                          >
                            <Box
                              p={1.5}
                              bg={childActive ? "white" : "whiteAlpha.200"}
                              color={childActive ? "#C41E3A" : "white"}
                              borderRadius="8px"
                            >
                              {child.icon}
                            </Box>
                            <Text ml={3} fontSize="sm" fontWeight={childActive ? "bold" : "medium"}>
                              {child.name}
                            </Text>
                          </Flex>
                        </MotionBox>
                      </NavLink>
                    );
                  })}
                </VStack>
              </MotionBox>
            )}
          </AnimatePresence>
        </Box>
      );
    }

    // ── Flat nav item ──────────────────────────────────────────────────────
    return (
      <Box key={index} mb={1}>
        <NavLink
          to={(item.layout || "/admin") + item.path}
          onClick={(e) => {
            const token =
              localStorage.getItem("authToken") ||
              sessionStorage.getItem("authToken");
            if (!token) {
              e.preventDefault();
              window.location.href = "/auth/signin";
            }
          }}
          end
        >
          <MotionBox
            layout
            whileHover={{ x: isExpanded ? 6 : 0 }}
            whileTap={{ scale: 0.98 }}
            cursor="pointer"
            borderRadius="14px"
            overflow="hidden"
          >
            <Flex
              align="center"
              justify={isExpanded ? "space-between" : "center"}
              px={isExpanded ? 4 : 2}
              py={3}
              bg={active ? "#C41E3A" : "transparent"}
              color={active ? "white" : "gray.300"}
              _hover={{ bg: active ? "#8B0000" : "whiteAlpha.100" }}
              transition="all 0.22s"
            >
              <HStack spacing={isExpanded ? 3 : 0}>
                {iconBox(active)}
                {isExpanded && (
                  <Text fontSize="sm" fontWeight="medium">
                    {item.name}
                  </Text>
                )}
              </HStack>
            </Flex>
          </MotionBox>
        </NavLink>
      </Box>
    );
  };

  const sidebarContent = (
    <Flex
      direction="column"
      h="100vh"
      bg="gray.900"
      color="white"
      width={isExpanded ? "280px" : "80px"}
      transition="width 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
      overflow="hidden"
      boxShadow="2xl"
      position="relative"
    >
      {/* Header */}
      <Flex
        align="center"
        justify={isExpanded ? "space-between" : "center"}
        p={isExpanded ? 5 : 4}
        h="80px"
        borderBottom="1px solid"
        borderColor="gray.800"
        position="relative"
      >
        <MotionFlex layout align="center" gap={3}>
          <CocacolaLogo isExpanded={isExpanded} />
        </MotionFlex>

        {isMobile && isOpen && (
          <DrawerCloseButton
            position="absolute"
            right={3}
            top="50%"
            transform="translateY(-50%)"
            color="white"
            size="lg"
          />
        )}
      </Flex>

      {/* Navigation Links */}
      <Box
        flex={1}
        overflowY="auto"
        overflowX="hidden"
        px={2}
        py={4}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "3px",
          },
        }}
      >
        <VStack align="stretch" spacing={1}>
          {menuItems.map((item, i) => renderNavItem(item, i))}
        </VStack>
      </Box>

      {/* Footer logos */}
      <Flex
        justify="center"
        align="center"
        gap={isExpanded ? { base: 2, md: 3, lg: 4 } : 2}
        py={{ base: 3, md: 4 }}
        px={2}
        borderTop="1px solid"
        borderColor="gray.800"
        flexWrap="wrap"
      >
        <Box
          w={isExpanded ? { base: "90px", sm: "100px", md: "110px", lg: "130px" } : { base: "45px", md: "55px" }}
          h={isExpanded ? { base: "45px", sm: "50px", md: "52px", lg: "55px" } : { base: "40px", md: "45px" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Image src={OrgLogo} alt="ORG Logo" objectFit="contain" w="160px" h="80px" />
        </Box>
        <Box
          w={isExpanded ? { base: "90px", sm: "100px", md: "110px", lg: "110px" } : { base: "35px", md: "35px" }}
          h={isExpanded ? { base: "45px", sm: "50px", md: "52px", lg: "55px" } : { base: "40px", md: "45px" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Image src={FactsLogo} alt="Facts Logo" objectFit="contain" w="120px" h="40px" />
        </Box>
      </Flex>
    </Flex>
  );

  // Mobile
  if (isMobile) {
    return (
      <>
        <IconButton
          aria-label="Open menu"
          icon={<Menu size={24} />}
          position="fixed"
          left={4}
          top={4}
          zIndex={9999}
          bg="#F40009"
          color="white"
          shadow="2xl"
          borderRadius="full"
          width="44px"
          height="44px"
          onClick={onOpen}
          _hover={{ bg: "#C41E3A" }}
          _active={{ bg: "#8B0000" }}
        />
        <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
          <DrawerOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
          <DrawerContent bg="gray.900" maxW="280px" margin={0} boxShadow="2xl">
            <DrawerBody p={0}>{sidebarContent}</DrawerBody>
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  // Desktop
  return (
    <>
      <Box position="fixed" left={0} top={0} h="100vh" zIndex={1000}>
        {sidebarContent}
        <IconButton
          icon={
            <MotionBox animate={{ rotate: isExpanded ? 0 : 180 }} transition={{ duration: 0.3 }}>
              {isExpanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
            </MotionBox>
          }
          size="md"
          variant="solid"
          bg="#F40009"
          color="white"
          _hover={{ bg: "#C41E3A" }}
          _active={{ bg: "#8B0000" }}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-label="Toggle sidebar"
          position="absolute"
          right="-20px"
          top="25px"
          zIndex={1001}
          borderRadius="full"
          boxShadow="2xl"
          width="32px"
          height="32px"
          minW="32px"
        />
      </Box>
      <Box
        w={isExpanded ? "280px" : "80px"}
        transition="width 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
        flexShrink={0}
      />
    </>
  );
}
